'use client';

import { Button } from '@/components/ui/button';
import { Button as AntdButton } from 'antd';
import { PanelRightOpen } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/loading';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import ShareModal from '@/components/deep-mining/share-modal';
import DmTooltip from '../deep-mining/dm-tooltip';
import Image from 'next/image';
import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import AgentModeTag from '../deep-mining/agent-mode-tag';
import ThreadModelTag from '../deep-mining/thread-model-tag';
import { updateProjectName } from '@/services/thread';
import { IconWrapper } from '../ui/icon-wrapper';
import EditIcon from '/public/edit-name.svg';
import ShareIcon from '/public/share.svg';
import LeftAgentIcon from '/public/left-agent-icon.svg';
import { useI18n } from '@/hooks/use-i18n';

interface ThreadSiteHeaderProps {
  threadId: string;
  projectId: string;
  projectName: string;
  enableShareCreateTemplate?: boolean;
  onProjectRenamed?: (newName: string) => void;
  isMobileView?: boolean;
  threadMetadata?: Record<string, any>;
  shareDisabled?: boolean;
  shareMessage?: string;
}

export function SiteHeader({
  threadId,
  projectId,
  projectName,
  onProjectRenamed,
  isMobileView,
  threadMetadata,
  shareDisabled,
  shareMessage,
}: ThreadSiteHeaderProps) {
  const { t } = useI18n('thread');
  const [editName, setEditName] = useState(projectName);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);

  const isMobile = useIsMobile() || isMobileView;

  const openShareModal = () => {
    setShowShareModal(true);
  };

  const openRenameModal = () => {
    setEditName(projectName);
    setShowRenameModal(true);
  };

  const saveNewName = async () => {
    if (editName.trim() === '') {
      setEditName(projectName);
      setShowRenameModal(false);
      return;
    }

    if (editName !== projectName) {
      try {
        if (!projectId) {
          toast.error('Cannot rename: Project ID is missing');
          setEditName(projectName);
          setShowRenameModal(false);
          return;
        }

        const updatedProject = await updateProjectName(projectId, editName);
        if (updatedProject) {
          onProjectRenamed?.(editName);
          toast.success(t('siteHeader.renameSuccess'));
        } else {
          throw new Error('Failed to update project');
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to rename project';
        console.error('Failed to rename project:', errorMessage);
        toast.error(errorMessage);
        setEditName(projectName);
      }
    }

    setShowRenameModal(false);
  };

  return (
    <>
      <header
        className={cn(
          'bg-background sticky top-0 flex h-14 shrink-0 items-center gap-2 z-20 w-full',
          false && isMobile && 'px-2',
        )}
      >
        <div className="flex flex-1 min-w-0 items-center gap-1 px-3">
          {!projectName || projectName === 'Project' ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <>
              {threadMetadata?.last_source_msg_type === 'api-key' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-[#7171EE] cursor-default">
                        <LeftAgentIcon className="w-4 h-4 text-white" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('components:sidebar.externalAgentSession')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <DmTooltip tooltip={projectName}>
                <div className="text-base font-medium overflow-hidden whitespace-nowrap text-ellipsis">
                  {projectName}
                </div>
              </DmTooltip>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 pr-4">
          {false && isMobile ? (
            // Mobile view - only show the side panel toggle
            <Button
              variant="ghost"
              size="icon"
              // onClick={onToggleSidePanel}
              className="h-9 w-9 cursor-pointer"
              aria-label="Toggle computer panel"
            >
              <PanelRightOpen className="h-4 w-4" />
            </Button>
          ) : (
            // Desktop view - show all buttons with tooltips
            <>
              {/* Agent Mode */}
              <ThreadModelTag model={threadMetadata?.model} />
              <AgentModeTag mode={threadMetadata?.agent_mode} />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={openRenameModal}
                      className="h-8 w-8 cursor-pointer rounded-full"
                    >
                      <IconWrapper icon={EditIcon} size={20} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('siteHeader.editThreadName')}</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    {/*按钮禁用时 tooltip 不生效，增加一个 div 盒子就可以解决 */}
                    <div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={openShareModal}
                        className="h-8 w-8 cursor-pointer rounded-full"
                        disabled={shareDisabled}
                      >
                        <IconWrapper icon={ShareIcon} size={20} />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{shareMessage || t('siteHeader.shareThread')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
      </header>
      <ShareModal
        isOpen={showShareModal}
        type="chat"
        onClose={() => setShowShareModal(false)}
        data={{ id: threadId }}
      />
      <RenameModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        currentName={projectName}
        editName={editName}
        setEditName={setEditName}
        onSave={saveNewName}
      />
    </>
  );
}

// 新增修改标题的 Modal 组件
interface RenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  editName: string;
  setEditName: (name: string) => void;
  onSave: () => void;
}

const RenameModal = (props: RenameModalProps) => {
  const { isOpen, onClose, currentName, editName, setEditName, onSave } = props;
  const { t } = useI18n('thread');
  const inputRef = useRef<HTMLInputElement>(null);

  // const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter') {
  //     onSave();
  //   } else if (e.key === 'Escape') {
  //     onClose();
  //   }
  // };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={t('siteHeader.renameTitle')}
      width={400}
      height={240}
      footer={null}
      className="rename-modal"
      classNames={{
        content: '!pb-[24px]',
      }}
    >
      <div className="flex items-center justify-center flex-col h-full space-y-4 px-[44px] pt-[8px]">
        <div className="w-full mb-[32px]">
          <Input
            ref={inputRef}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            // onKeyDown={handleKeyDown}
            className="h-10 text-base font-medium focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-0 focus-visible:outline-none border-none bg-[#F8F8FA]"
            maxLength={50}
            placeholder={t('siteHeader.renamePlaceholder')}
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-2 w-full">
          <AntdButton type="primary" onClick={onSave} size="large">
            {t('siteHeader.confirm')}
          </AntdButton>
        </div>
      </div>
    </Modal>
  );
};
